import { Heading, Box, Flex, Button, Link } from "@chakra-ui/react";
import LandingContent from "../../../components/landing-content";
import { H3, LB } from "../../../components/text-formatting";
import { siteConfig } from "../../../configuration/config";

export const contactEmail = siteConfig.adminEmail ?? "legal@talkio.ai";

export default function Terms() {
  const TalkioTerms = () => (
    <>
      <H3 text="Cancellations and Refunds" />
      We aim to be fair and flexible. If something went wrong with your subscription, feel free to
      reach out—we review all cases individually.
      <H3 text="Ownership" />
      The product is offered by:
      <LB />
      Aidia ApS
      <br />
      Pærevangen 15 1 mf
      <br />
      2765 Smørum
      <br />
      Denmark
      <LB />
      By using {siteConfig.serviceName}, you acknowledge that you have read, understood, and agree
      to be bound by these Terms and Conditions.
    </>
  );

  return (
    <LandingContent>
      <Heading mb={3} fontSize={"xl"}>
        {siteConfig.serviceName} Terms and Conditions
      </Heading>

      {/* Navigation Links */}
      <Flex mb={6} mt={4}>
        <Link href="#individual-terms" mr={6}>
          <Button size="sm" variant="outline">
            Individual Users Terms
          </Button>
        </Link>
        <Link href="#business-terms">
          <Button size="sm" variant="outline">
            Business & Schools Terms
          </Button>
        </Link>
      </Flex>

      <div>
        Last Updated: May 1st, 2025
        <LB />
        Welcome to {siteConfig.serviceName}. Please read these Terms and Conditions carefully before
        using our language learning web application
        {`("${siteConfig.serviceName}", "the Application", "our", "us", or "we"). By accessing or using the Application, you ("User", "you", or "your")`}
        agree to be bound by these Terms and Conditions.
        <Box id="individual-terms" pt={4}>
          <Heading as="h2" size="md" mb={4}>
            Terms for Individual Users
          </Heading>
          <H3 text="Acceptance of Terms and Conditions" />
          By using {siteConfig.serviceName}, you accept and agree to be bound and abide by these
          Terms and Conditions. If you do not agree to these Terms and Conditions, you must not
          access or use the Application.
          <H3 text="Pricing" />
          {siteConfig.serviceName} is a paid service. Payment is handled by Stripe Inc. on behalf of
          Aidia ApS. By using {siteConfig.serviceName}, you acknowledge and agree to pay for access
          and usage of the Application.
          <H3 text="User Account" />
          To use {siteConfig.serviceName}, you must create an account by providing accurate and
          complete information. You are solely responsible for maintaining the confidentiality of
          your account credentials and for any activities that occur under your account. You agree
          to notify us immediately of any unauthorized use of your account or any other breach of
          security. Each user is allowed to create and maintain only one user account. Creating
          multiple accounts for a single user is not permitted.
          <H3 text="Personal Use Only" />
          {siteConfig.serviceName} is for personal, non-commercial use only. You may not use the
          Application for any commercial purpose, including but not limited to selling access to the
          Application, collecting fees for access to the Application, or using the Application to
          provide language tutoring or other services to third parties. However, we do offer a
          separate service for businesses and schools.
          <H3 text="Conversation Storage" />
          {siteConfig.serviceName} stores your conversations with our artificial tutors to enhance
          your learning experience and to allow you to review past conversations. By using the
          Application, you consent to the storage of your conversations for this purpose.
          <H3 text="Fair Use" />
          You agree to use {siteConfig.serviceName} responsibly and in accordance with these Terms
          and Conditions. While we aim to provide generous access to features such as the AI chat
          and pronunciation tool, we reserve the right to implement usage limits if we detect
          patterns that indicate potential misuse or activity outside the intended purpose of the
          application. These measures are designed to ensure fair and effective access for all
          users. Should any restrictions be applied to your account, you may contact us to review
          your usage and request adjustments if your activities are consistent with fair use. Note
          that any applicable limits may vary based on your subscription plan.
          <LB />
          You may not use the Application to:
          <LB />
          a. Engage in any activity that violates any law or regulation;
          <br />
          b. Transmit or upload any material that is harmful, offensive, or inappropriate;
          <br />
          c. Harass, threaten, or intimidate others;
          <br />
          d. Impersonate any person or entity or misrepresent your affiliation with a person or
          entity;
          <br />
          e. Interfere with or disrupt the operation of the Application or the servers or networks
          connected to the Application;
          <br />
          f. Attempt to gain unauthorized access to any portion of the Application or any systems
          connected to the Application;
          <br />
          g. Use the Application in any way that could damage, disable, overburden, or impair the
          site;
          <br />
          h. Use the Application to provide language tutoring or other services to third parties
          without our consent or signing up for a business account; or
          <br />
          i. Use the Application to purposes not related to language learning.
          <H3 text="AI-Generated Content Disclaimer" />
          {siteConfig.serviceName} utilizes artificial intelligence to generate content, including
          conversations with artificial tutors. While we strive to provide a respectful and
          inclusive learning environment, there may be instances where the AI-generated content
          might be perceived as offensive, inappropriate, or otherwise objectionable. We disclaim
          any liability arising from the AI-generated content and any offense or harm it may cause.
          We encourage users to report any offensive or inappropriate content to us at{" "}
          {contactEmail} so that we can continue to improve the Application and ensure a positive
          user experience. Furthermore, the AI tutor a may present information as facts, but the AI
          tutor is not a human and may not have the most up-to-date information. We encourage users
          to verify information presented by the AI tutor with other sources.
          <H3 text="Age Limit" />
          {siteConfig.serviceName} is intended for use by individuals 13 years of age or older. By
          using the Application, you represent and warrant that you are at least 13 years of age. If
          you are under 13 years of age, you may not use the Application unless your parent or
          guardian are supervising your use.
        </Box>
        <Box id="business-terms" pt={8} mt={4}>
          <Heading as="h2" size="md" mb={4}>
            Terms for Businesses and Schools
          </Heading>
          <H3 text="Business and Educational Use" />
          {siteConfig.serviceName} for Schools and Businesses is designed for educational
          institutions, language schools, and businesses seeking to provide language learning
          opportunities for their students or employees. The following terms apply specifically to
          business and educational accounts. Please note that students using{" "}
          {siteConfig.serviceName} under a school or business account are subject to the same usage
          terms as individual users.
          <H3 text="Account Administration" />
          Business and school accounts allow for designated administrators who can manage user
          licenses, monitor usage, and access administrative features. Administrators are
          responsible for managing student/employee accounts, including inviting users, assigning
          licenses, and monitoring usage.
          <H3 text="Licensing and Pricing" />
          Business pricing is based on the number of user licenses purchased. Each license permits
          one unique user to access the platform. Sharing accounts between multiple users is
          strictly prohibited. Licenses are non-transferable.
          <H3 text="Data and Privacy for Organizations" />
          School and business administrators may have access to usage statistics and learning
          progress data for their users. This data is provided to help administrators monitor
          engagement and effectiveness. Administrators do not have access to the specific content of
          conversations between users and AI tutors by default. However, organizations may opt in to
          enable access to conversation content through their settings. If this feature is enabled,
          the organization is responsible for clearly informing users that their conversations may
          be accessed by designated administrators.
          <H3 text="Fair Use Policy" />
          The service is offered without a specific upper limit on individual student usage,
          provided it falls within fair use boundaries. In the unlikely event that the average
          monthly character consumption in chat and pronunciation features across all accounts
          exceeds 300,000 characters per user, Aidia reserves the right to implement a usage cap of
          300,000 characters per user.
          <H3 text="Organizational Responsibilities" />
          Organizations are responsible for ensuring that all users comply with these Terms and
          Conditions. Organizations must ensure that all users are of appropriate age (13+) or are
          supervised by an adult. Organizations must obtain appropriate consent from users or their
          guardians regarding data collection and processing in accordance with applicable laws.
          Organizations must also comply with the EU AI Act and ensure that {
            siteConfig.serviceName
          }{" "}
          is not used as the sole basis for assessment or grading without meaningful human
          involvement. Furthermore, organizations must clearly inform users that they are
          interacting with an AI system to avoid any risk of deception.
        </Box>
        <Box pt={8}>
          <Heading as="h2" size="md" mb={4}>
            General Terms
          </Heading>
          <H3 text="Termination" />
          We reserve the right, in our sole discretion, to terminate your access to the Application
          and your account without notice for any reason, including but not limited to your
          violation of these Terms and Conditions.
          <H3 text="Changes to Terms and Conditions" />
          We reserve the right, at our sole discretion, to modify or replace these Terms and
          Conditions at any time. By continuing to access or use the Application after such
          modifications, you agree to be bound by the updated Terms and Conditions.
          <H3 text="Governing Law and Jurisdiction" />
          These Terms and Conditions shall be governed by and construed in accordance with the laws
          of Denmark, without regard to its conflict of law provisions.
          <H3 text="Contact Information" />
          If you have any questions about these Terms and Conditions, please contact us at{" "}
          {contactEmail}.
          <TalkioTerms />
        </Box>
      </div>
    </LandingContent>
  );
}
